import React from "react";
import {
    Box,
    Container,
    Flex,
    Text,
    Input,
    HStack,
    IconButton,
    VStack,
    Card,
    CardBody,
    Divider,
    Avatar,
    Badge,
    GridItem,
    Grid,
} from "@chakra-ui/react";
import { FaSearch, FaComments, FaUsers, FaStar, FaTrophy } from "react-icons/fa";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { fetchCommunityStats, fetchLeaderboard } from "../../api/mockForumApi"; // Reusing forum API for sidebar data

const MotionBox = motion(Box);

const Layout = ({ children, title }) => {
    const [communityStats, setCommunityStats] = useState({
        activeUsers: 0,
        totalPosts: 0,
        expertMembers: 0,
    });
    const [leaderboard, setLeaderboard] = useState([]);

    useEffect(() => {
        const getSidebarData = async () => {
            const [statsData, leaderboardData] = await Promise.all([
                fetchCommunityStats(),
                fetchLeaderboard(),
            ]);
            setCommunityStats(statsData);
            setLeaderboard(leaderboardData);
        };

        getSidebarData();

        const interval = setInterval(async () => {
            const statsData = await fetchCommunityStats();
            setCommunityStats(statsData);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    return (
        <Box
            minH="100vh"
            bg="gray.900"
            color="white"
            backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,0,150,0.10) 0%, transparent 60%), radial-gradient(circle at 75% 75%, rgba(0,150,255,0.10) 0%, transparent 60%)"
        >
            <Box bg="gray.800" borderBottom="1px" borderColor="gray.700">
                <Container maxW="1200px" py={4}>
                    <Flex
                        direction={{ base: "column", md: "row" }}
                        justify="space-between"
                        align={{ base: "flex-start", md: "center" }}
                        gap={4}
                    >
                        <Text fontSize="2xl" fontWeight="bold" color="purple.400">
                            {title || "WAGER STREET"}
                        </Text>
                        <HStack spacing={4} w={{ base: "full", md: "auto" }}>
                            <Input
                                placeholder="Search..."
                                size="sm"
                                maxW={{ base: "full", md: "300px" }}
                                bg="gray.700"
                                border="none"
                                _placeholder={{ color: "gray.400" }}
                            />
                            <IconButton
                                icon={<FaSearch />}
                                size="sm"
                                colorScheme="purple"
                                aria-label="Search"
                            />
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="1200px" py={8}>
                <Grid templateColumns={{ base: "1fr", lg: "1fr 300px" }} gap={8}>
                    <GridItem>
                        {children}
                    </GridItem>

                    <GridItem>
                        <VStack spacing={6} align="stretch">
                            <MotionBox
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <Card bg="gray.800" border="1px" borderColor="gray.700">
                                    <CardBody>
                                        <VStack align="start" spacing={4}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="purple.400"
                                            >
                                                Community Stats
                                            </Text>
                                            <Divider borderColor="gray.600" />
                                            <VStack align="start" spacing={3} w="full">
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaUsers color="#68D391" />
                                                        <Text>Active Users</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="green.400"
                                                    >
                                                        {communityStats.activeUsers.toLocaleString()}
                                                    </Text>
                                                </HStack>
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaComments color="#9F7AEA" />
                                                        <Text>Total Posts</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="purple.400"
                                                    >
                                                        {communityStats.totalPosts.toLocaleString()}
                                                    </Text>
                                                </HStack>
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaStar color="#F6E05E" />
                                                        <Text>Expert Members</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="yellow.400"
                                                    >
                                                        156
                                                    </Text>
                                                </HStack>
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </MotionBox>

                            <MotionBox
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <Card bg="gray.800" border="1px" borderColor="gray.700">
                                    <CardBody>
                                        <VStack align="start" spacing={4}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="purple.400"
                                            >
                                                Leaderboard
                                            </Text>
                                            <Divider borderColor="gray.600" />
                                            <VStack align="start" spacing={3} w="full">
                                                {leaderboard.map((user, i) => (
                                                    <HStack key={user.name} justify="space-between" w="full">
                                                        <HStack>
                                                            <Avatar size="sm" name={user.name} src={user.avatar} />
                                                            <Text>{user.name}</Text>
                                                        </HStack>
                                                        <HStack>
                                                            <FaTrophy color={i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "#68D391"} />
                                                            <Text
                                                                color={i === 0 ? "yellow.400" : i === 1 ? "gray.400" : i === 2 ? "orange.400" : "green.400"}
                                                                fontWeight="bold"
                                                            >
                                                                {user.score}
                                                            </Text>
                                                        </HStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </MotionBox>
                        </VStack>
                    </GridItem>
                </Grid>
            </Container>
        </Box>
    );
};

export default Layout;